import { BriefingSendAtApiModel } from '../../../api/briefings/model/briefing-send-at-api.model';
import { BriefingRecurrencePatternModel } from './briefing-recurrence-pattern.model';
import moment from 'moment-timezone';
import { BriefingDateRangeModel } from './briefing-date-range.model';
import * as _ from 'underscore';
import { momentTimeZoneMap, agilityTimeZoneMap } from '../../../Common/Forms/data/time-zones';

export class BriefingSendAtModel {
    sendNow: boolean;
    sendLater: number; // Unix timestamp
    timeZone: string;
    recurring: BriefingRecurrencePatternModel;
    range: BriefingDateRangeModel;
    allowPastContent: boolean;

    private readonly dateFormat: string = 'MMMM DD, YYYY hh:mm A z';

    /**
     * Class Constructor
     */
    constructor(apiModel?: BriefingSendAtApiModel) {
        if (apiModel) {
            // The backend doesn't save nulls, so we need to map to the default values where nulls were used
            this.sendNow = apiModel.sendNow ? true : null;
            this.sendLater = (apiModel.sendLater !== 0 && !!apiModel.sendLater ) ? apiModel.sendLater * 1000 : null;
            this.timeZone = apiModel.timeZone;
            this.allowPastContent = !!apiModel.allowPastContent;
            if (apiModel.recurring && apiModel.recurring.frequencyType) {
                this.recurring = new BriefingRecurrencePatternModel(apiModel.recurring, this.timeZone);
            } else {
                this.recurring = null;
            }
            if (apiModel.range) {
                this.range = new BriefingDateRangeModel(apiModel.range);
            } else {
                this.range = null;
            }
        } else {
            this.sendNow = true;
            this.sendLater = null;
            this.recurring = null;
            this.timeZone = null;
            this.range = null;
            this.allowPastContent = false;
        }
    }

    /**
     * Compares this BriefingSendAtModel to another
     */
    public equals(other: BriefingSendAtModel): boolean {
        if (this.sendNow !== other.sendNow) {
            return false;
        }

        if (this.sendLater !== other.sendLater) {
            return false;
        }

        if (this.timeZone !== other.timeZone) {
            return false;
        }

        if (this.recurring !== null && other.recurring !== null && !this.recurring.equals(other.recurring)) {
            return false;
        }

        if (this.allowPastContent !== other.allowPastContent) {
            return false;
        }

        if (this.range && other.range) {
            if (!this.range.equals(other.range)) {
                return false;
            }
        } else if (!!this.range !== !!other.range) {
            // if one of them has a range and the other does not
            return false;
        }

        return true;
    }

    /**
     * Converts this front end model to the API version (BriefingSendAtApiModel)
     */
    public toBriefingSendAtApiModel(): BriefingSendAtApiModel {
        return {
            sendNow: this.sendNow,
            sendLater: !!this.sendLater ? this.sendLater / 1000 : null,
            recurring: this.recurring ? this.recurring.toBriefingRecurrencePatternApiModel() : null,
            timeZone: this.timeZone,
            range: this.range ? this.range.toBriefingDateRangeApiModel() : null,
            allowPastContent: this.allowPastContent
        };
    }

    /**
     * Returns the send at value as a string
     */
    public getDisplayString(format?: string): string {
        let returnString: string;

        if (this.sendNow) {
            returnString = 'Briefing.immediate';
        } else if (this.sendLater) {
            returnString = moment(this.sendLater).tz(this.timeZone).format(format ? format : this.dateFormat);
        } else {
            returnString = this.formatRecurringTime(this.recurring);
        }

        return returnString;
    }

    /**
     * Shortcut for setting "sendNow true"
     */
    public setSendNow(): void {
        this.sendNow = true;
        this.sendLater = null;
        this.recurring = null;
    }

    /**
     * Shortcut for setting sendLater to a date
     */
    public setSendLater(date: Date) {
        if (date) {
            this.sendLater = date.getTime();
        } else {
            this.sendLater = new Date().getTime();
        }
        this.sendNow = null;
        this.recurring = null;
    }

    /**
     * Shortcut for setting recurring pattern
     */
    public setRecurring(recurring: BriefingRecurrencePatternModel) {
        this.recurring = recurring;

        if (recurring.timeZone) {
            this.timeZone = recurring.timeZone;
        }

        this.sendNow = null;
        this.sendLater = null;
    }

    /**
     * Formats recurring send time in hours and minutes
     *
     * @param recurringModel contains time data to format
     *
     * @returns string - formatted time
     */
    private formatRecurringTime(recurringModel: BriefingRecurrencePatternModel): string {

        let timeAsString: string = '';
        let stringVal: string;
        for (let i = 0; i < recurringModel.times.length; i++) {
            stringVal = recurringModel.times[i].asStringWithMeridians();
            if (timeAsString === '') {
                timeAsString = stringVal;
            } else {
                timeAsString = timeAsString + ', ' + stringVal;
            }
        }

        return timeAsString;
    }

    /**
     * Determines whether or not the timezone
     * is set to a valid string
     */
    public get hasValidTimezone(): boolean {
        return this.timeZone && (momentTimeZoneMap.has(this.timeZone) || agilityTimeZoneMap.has(this.timeZone));
    }
}
